import React, { useReducer, useContext, useRef, useEffect } from 'react';
import { GlobalContext } from '../globalContext';
import { endpoints, apiClient } from '../utils/constants';
import Pin from '../assets/icons/svg/map-pin-line.svg';
import ArrowRightWhite from "../assets/icons/svg/arrow-right-line-w.svg";
import { trackEvent } from '../utils/gtmTracker';


export default function AddressForm() {
  const { store, updateStore, t } = useContext(GlobalContext);
  const [ state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      inputValue: '',
      suggestions: [],
      selectedAddress: null,
      showErrorModal: false,
      showSelectAddressModal: false,
      timer: null
    }
  );
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const inputStoppedTimerRef = useRef(null);
  const lastTrackedInputRef = useRef("");


  useEffect(() => {
    if (store.focusAddressInput && inputRef.current) {
      inputRef.current.focus();
      updateStore({ focusAddressInput: false });
    }
  }, [store.focusAddressInput]);

  // Rimuovere solo lettere accentate (mantiene apostrofi solo tra lettere)
  const stripDiacritics = (str = "") =>
    str
      .normalize("NFD")                // decomposizione: "é" → "e◌́"
      .replace(/[\u0300-\u036f]/g, "") // rimuove i segni diacritici
      .replace(/\s+/g, " ") // Normalizza spazi multipli
      .trim(); // rimuove spazi all'inizio e alla fine della stringa qualora presenti.

  
  const handleChange = (e) => {
    const value = e.target.value;
    const trimmed = value.trim();
    const lastChar = value.slice(-1);


    updateState({ inputValue: value })

    if (state.timer) {
      clearTimeout(state.timer)
    }

    // ❌ NON triggerare nulla se input "vuoto" o solo spazi
    if (trimmed.length === 0) {
      updateState({ suggestions: [] });
      return;
    }

    // ❌ Input troppo lungo (es. incollato o spam)
    if (trimmed.length > 100) {
      updateState({ suggestions: [] });
      return;
    }

    // ❌ Input "sporco" → solo simboli inutili
    const hasValidText = /[a-zA-Z0-9]/.test(trimmed);
    if (!hasValidText) {
      updateState({ suggestions: [] });
      return;
    }

    // Filtra input con troppi simboli rispetto al testo utile
    const onlyUsefulChars = trimmed.match(/[a-zA-Z0-9]/g) || [];
    const usefulRatio = onlyUsefulChars.length / trimmed.length;

    if (usefulRatio < 0.25) { // meno del 25% è testo utile
      updateState({ suggestions: [] });
      return;
    }

    // ❌ Blocca se ci sono troppi caratteri identici consecutivi
    const repeatedCharMatch = trimmed.match(/(.)\1{6,}/); // es. 'aaaaaa' o '111111'
    if (repeatedCharMatch) {
      updateState({ suggestions: [] });
      return;
    }

    // Evento: utente ha smesso di digitare (debounce 2s, robusto), SOLO se input valido
    if (inputStoppedTimerRef.current) {
      clearTimeout(inputStoppedTimerRef.current);
    }

    inputStoppedTimerRef.current = setTimeout(() => {
      if (trimmed.length > 5 && trimmed !== lastTrackedInputRef.current) {
        trackEvent("address_input_last_digit", {
          insertionAddress: value
        });
        lastTrackedInputRef.current = trimmed;
      }
    }, 2000);

    if (trimmed.length >= 5) {
      const timerX = setTimeout(() => {
        // ✅ SOLO ORA normalizziamo per il backend
        const sanitized = stripDiacritics(trimmed);
        //console.log('[handleChange] Trigger via debounce:', trimmed);
        //fetchAddresses(value);

        // 🔍 DEBUG log
        //console.log("[AddressForm] Input originale:", trimmed);
        //console.log("[AddressForm] Input normalizzato per backend:", sanitized);

        fetchAddresses(sanitized);
            
      }, 1000);
      updateState({ timer: timerX });
    } else {
      //console.log('[handleChange] Input troppo corto, svuoto suggerimenti');
      updateState({ suggestions: [] });
    }

  };


  // ✅ Chiamata al servizio
  const fetchAddresses = (inputAddress) => {

    
    const requestURL = `${endpoints.getListAddresses}?address=${encodeURIComponent(inputAddress)}`;
    
    //console.log('requestURL', requestURL);
    
    apiClient
      .get(requestURL)
      .then((data) => {
        //console.log('[fetchAddresses] Response ricevuta:', data);

        const list = data.list || [];
        const suggestions = list.map((item) => ({
          codeTopClient: item.codiceTopCliente,
          address: item.indirizzo
        }));

        //console.log('[fetchAddresses] Suggerimenti generati:', suggestions);
        updateState({ suggestions });

      })
      .catch((err) => {
        console.error('Errore fetch:', err);
        updateState({ suggestions: [] });
      });
  };



  const handleSelect = (item) => {
    updateState({ 
      selectedAddress: item,
      inputValue: item.address,
      suggestions: []
    });

     updateStore({
      selectedAddress: item.address,
      selectedCodeTopClient: item.codeTopClient
    });

  };

  const handleSubmit = () => {

    if (!state.selectedAddress) {
      updateState({ showSelectAddressModal: true });
      return;
    }

    const codeSelection = state.selectedAddress.codeTopClient;

    updateStore({ isLoading: true });

    const requestPromise = apiClient.get(`${endpoints.verifyCoverage}?codiceTopCliente=${encodeURIComponent(codeSelection)}`);    
    
    requestPromise
    .then((data) => {
        
        if (!data || data === null || data?.statoCopertura === null) { // prima era statoFtth
          throw new Error("Risposta nulla dal server");
        }

        const rawStatus = data?.statoCopertura;
        
        let mappedStatus = "no_network";

        if (rawStatus === "FTTH_ATTIVO") { 
          mappedStatus = "coveredFtth"; 
        } else if (rawStatus === "FTTH_ATTIVO_FIBERSTREAM") {
          mappedStatus = "coveredFiberstream";
        } else if (rawStatus === "FTTH_PIANIFICATO_FTTC_ATTIVO" || rawStatus === "FTTH_PIANIFICATO_FTTC_NON_ATTIVO") { 
          mappedStatus = "fttc_arrivingFtth";  
        } 
          else if (rawStatus === "FTTH_NON_ATTIVO_FTTC_ATTIVO") {
          mappedStatus = "fttc_notArrivingFtth";   
        }
          else if (rawStatus === "FTTH_NON_ATTIVO_FTTC_PIANIFICATO") {
          mappedStatus = "fttc_planned";
        }
          else if (rawStatus === "FTTH_NON_ATTIVO_FTTC_NON_ATTIVO") {
          mappedStatus = "no_network";
        } 


        const normalizeOffers = (offersNode, fallbackTech) => {
          const list = Array.isArray(offersNode?.offerte)
            ? offersNode.offerte
            : Array.isArray(offersNode)
              ? offersNode
              : (Array.isArray(offersNode?.page) ? offersNode.page : []);

          return list.map((offer) => ({
            ...offer,
            tipoOfferta: offer?.tipoOfferta || fallbackTech
          }));
        };

        const listOfOffers = [
          ...normalizeOffers(data?.offerteFttc, "FTTC"),
          ...normalizeOffers(data?.offerteFtth, "FTTH")
        ];

        updateStore({
          responseStatus: mappedStatus,
          listOfOffers
        });

        trackEvent("address_search", {
          indirizzo: state.inputValue,
          codiceTopCliente: state.selectedAddress.codeTopClient,
          statoTecnologia: rawStatus
        });
      })
      .catch((err) => {
        console.error("Errore chiamata coverage:", err);
        const errorMessage = err?.message || "unknown error";
        const statusCode = err?.response?.status;
        let errorString = '';
        if (statusCode) {
          errorString = `${statusCode}: ${errorMessage}`;
        } else {
          errorString = `Code not available: ${errorMessage}`;
        }
        // Tracciamento errore coverage search
        trackEvent("address_search_submit_error", {
          address_search_error: errorString,
          codiceTopCliente: state.selectedAddress?.codeTopClient || '',
          indirizzo: state.inputValue
        });

        if (err?.response?.status === 504 || errorMessage.includes("Risposta nulla")) {
          updateState({ 
            showErrorModal: true
          });
          updateStore({ isLoading: false });
          return;
        }

        updateState({ showErrorModal: true });
        updateStore({ isLoading: false });
      });

    const minimumDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    Promise.all([requestPromise, minimumDelay]).then(() => {
      updateStore({ isLoading: false });
    });
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        updateState({ suggestions: [] });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  return (
    <>
      
      <div className="address-container" ref={containerRef}>
        <h5 className="address-title">{t.addressTitle}</h5>
        <div className="address">
          <div className="address-input-box">
            <img src={Pin} alt="icon pin" className="address-icon" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Es. via Roma, 1 Varese"
              className="address-input"
              value={state.inputValue}
              onChange={handleChange}
            />

          </div>

          {state.suggestions.length > 0 && (
            <ul className="suggestions-list">
              {state.suggestions.map((addressObj) => (
                <li
                  key={addressObj.codeTopClient}
                  onClick={() => handleSelect(addressObj)}
                >
                 	{addressObj.address}
                </li>
              ))}
            </ul>
          )}
		      <button type="button" className="address-button" onClick={handleSubmit}>
            <img src={ArrowRightWhite} alt="icon Check" className="button-icon" />
          </button>
        </div>
        
      </div>

      {state.showErrorModal && (
        <div className="modal-overlay" onClick={() => updateState({ showErrorModal: false})}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => updateState({ showErrorModal: false})}
              aria-label={t.closePopup}
            >
              ✕
            </button>
            <p className="modal-text">
              {t.serviceUnavailable}
              <br />
              {t.tryAgainLater}
            </p>
          </div>
        </div>
      )}
      {state.showSelectAddressModal && (
        <div className="modal-overlay" onClick={() => updateState({ showSelectAddressModal: false})}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => updateState({ showSelectAddressModal: false})}
              aria-label={t.closePopup}
            >
              ✕
            </button>
            <p className="modal-text">
              {t.addressTooltip}
            </p>
          </div>
        </div>
      )}
    </>
  );
}